import { AudioEffect, createAudioEffectSystem } from 'modules-core/audioEffectSystem'
import { iUrlSystem, createUrlSystem } from 'modules-core/urlSystem'
import { FadeOptions } from './types'
import { createBlobSourceInstantiator } from './blobSourceInstantiator'
import { createEvent } from 'modules-core/utility'
import { eventSystem } from 'modules-core/eventSystem'
import { config, maxVolume, minVolume } from 'modules-core/config'
import log from 'modules-core/log'

// Register events.
eventSystem.add('setGlobalVolume')
eventSystem.add('setGlobalVolumeForElementAdjustment')
eventSystem.add('setLocalVolume')

const logger = log.getLogger('audioSystem')
export const COORDINATED_VOLUME_SCHEDULE_LEAD_TIME = 0.01

interface Args {
	urlSystem?: iUrlSystem
}
interface SetVolumeOptions {
	fadeDuration?: number
}
export const MUTE_TOGGLE_FADE_DURATION = 0.03
export const MUTE_TOGGLE_VOLUME_APPLY = {
	fadeDuration: MUTE_TOGGLE_FADE_DURATION,
}
export function createAudioSystem({
	urlSystem,
}: Args = {}) {

	urlSystem = urlSystem || createUrlSystem()

	const effectSystem = createAudioEffectSystem()
	const sourceInstantiator = createBlobSourceInstantiator({ effectSystem })

	const audioSystem = {
		effectSystem,
		effect: effectSystem.effect,
		init: async (useDefaultEffectFile = true) => {
			await effectSystem.init(useDefaultEffectFile)
		},
		setGlobalVolume: (value: number, options: SetVolumeOptions = {}) => {
			value = Number(value)
			if (Number.isNaN(value) || value < minVolume || value > maxVolume) {
				throw Error(`Invalid localVolume: '${value}'`)
			}
			logger.info(`Set global volume: ${value}`)
			const startTime = config.audioContext.currentTime + COORDINATED_VOLUME_SCHEDULE_LEAD_TIME
			if (options.fadeDuration === 0) {
				effectSystem.globalVolume.value = value
			} else {
				effectSystem.globalVolume.fadeToValue(
					value,
					options.fadeDuration,
					undefined,
					startTime,
				)
			}
			// Dispatch event so interfaces can update their volume controls.
			eventSystem.dispatch('setGlobalVolume', value)
			const adjustmentEvent: {
				fadeDuration?: number
				startTime?: number
				volume: number
			} = {
				fadeDuration: options.fadeDuration,
				volume: value,
			}
			if (options.fadeDuration !== 0) {
				adjustmentEvent.startTime = startTime
			}
			eventSystem.dispatch('setGlobalVolumeForElementAdjustment', adjustmentEvent)
		},
		setLocalVolume: (value: number, options: SetVolumeOptions = {}) => {
			value = Number(value)
			if (Number.isNaN(value) || value < minVolume || value > maxVolume) {
				throw Error(`Invalid localVolume: '${value}'`)
			}
			logger.info(`Set local volume: ${value}`)
			config.localVolume = value
			if (options.fadeDuration === 0) {
				effectSystem.localVolume.value = value
			} else {
				effectSystem.localVolume.fadeToValue(value, options.fadeDuration)
			}
			// Dispatch event so interfaces can update their volume controls.
			eventSystem.dispatch('setLocalVolume', value)
		},
		createAudioSource: (src: string, output?: AudioEffect.HasInput, fadeOptions?: FadeOptions, targetVolume?: number, elementId?: number) => {
			src = urlSystem.replaceLocalDomainAndPort(src)
			const source = sourceInstantiator.createAudioSource(src, output, fadeOptions, targetVolume, elementId)
			return source
		},
		toggleMute() {
			if (config.localVolume === 0) {
				// Muted. Restore last local volume.
				this.setLocalVolume(config.lastLocalVolume, MUTE_TOGGLE_VOLUME_APPLY)
			} else {
				// Not muted. Set last local volume (to restore on unmute) and mute.
				config.lastLocalVolume = config.localVolume
				this.setLocalVolume(0, MUTE_TOGGLE_VOLUME_APPLY)
			}
		},
	}
	return audioSystem
}

export type iAudioSystem = ReturnType<typeof createAudioSystem>
